import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Receipt, Download, FileText, Calendar, Euro, Coins,
  CheckCircle2, Clock, Search, ExternalLink, Info, ChevronLeft, ChevronRight,
} from "lucide-react";

const PAGE_SIZE = 10;

const STATUS_META = {
  transmitted: {
    label: "Trasmessa allo SDI",
    tone: "text-emerald-700 bg-emerald-50 border-emerald-200",
    icon: CheckCircle2,
  },
  not_transmitted: {
    label: "In coda per invio SDI",
    tone: "text-amber-800 bg-amber-50 border-amber-200",
    icon: Clock,
  },
};

const FMT_EUR = new Intl.NumberFormat("it-IT", {
  style: "currency",
  currency: "EUR",
});

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00Z" : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

export default function AccountBilling() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/account/billing");
      setInvoices(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile caricare le fatture");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const downloadXml = async (id, fullNumber) => {
    setDownloading(id);
    try {
      const res = await api.get(`/account/billing/${id}/xml`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fullNumber || id}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const status = e?.response?.status;
      toast.error(
        status === 410
          ? "XML non più disponibile. Contatta il supporto."
          : e?.response?.data?.detail || "Errore download"
      );
    } finally {
      setDownloading(null);
    }
  };

  const filtered = q
    ? invoices.filter((inv) => {
        const needle = q.toLowerCase();
        return (
          (inv.full_number || "").toLowerCase().includes(needle) ||
          (inv.package_name || "").toLowerCase().includes(needle) ||
          String(inv.numero || "").includes(needle)
        );
      })
    : invoices;

  // Reset alla prima pagina quando cambia la query di ricerca — evita di
  // ritrovarsi su una pagina vuota dopo aver filtrato il set.
  useEffect(() => { setPage(0); }, [q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageStart = page * PAGE_SIZE;
  const paged = useMemo(
    () => filtered.slice(pageStart, pageStart + PAGE_SIZE),
    [filtered, pageStart]
  );

  const totals = invoices.reduce(
    (acc, inv) => {
      acc.gross += inv.amount_gross_eur || 0;
      acc.credits += inv.credits || 0;
      return acc;
    },
    { gross: 0, credits: 0 }
  );

  return (
    <div className="space-y-6" data-testid="account-billing-page">
      {/* Header explainer */}
      <section className="rounded-xl border border-[#E8E4D9] bg-white p-6" data-testid="billing-explainer">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-50 grid place-items-center shrink-0">
            <Receipt className="w-5 h-5 text-[#1D4ED8]" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-semibold text-lg text-[#0A0A0A]">Fatture per acquisto crediti</h2>
            <p className="text-sm text-zinc-600 mt-1 max-w-2xl leading-relaxed">
              Qui trovi tutte le fatture elettroniche <strong>TD01</strong> che DASABO SRL ti ha emesso per gli acquisti pacchetti crediti. La versione ufficiale viene trasmessa al tuo cassetto fiscale tramite lo SDI; qui puoi comunque scaricare la copia XML in qualsiasi momento.
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <div className="rounded-lg border border-[#E8E4D9] bg-[#FAF9F6] p-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Fatture ricevute</div>
            <div className="mt-1 font-display font-extrabold text-2xl text-[#0A0A0A]" data-testid="stat-count">{invoices.length}</div>
          </div>
          <div className="rounded-lg border border-[#E8E4D9] bg-[#FAF9F6] p-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Speso complessivo</div>
            <div className="mt-1 font-display font-extrabold text-2xl text-[#0A0A0A]" data-testid="stat-gross">
              {FMT_EUR.format(totals.gross)}
            </div>
          </div>
          <div className="col-span-2 sm:col-span-1 rounded-lg border border-[#E8E4D9] bg-[#FAF9F6] p-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Crediti acquistati</div>
            <div className="mt-1 font-display font-extrabold text-2xl text-[#0A0A0A] flex items-center gap-2" data-testid="stat-credits">
              <Coins className="w-5 h-5 text-[#B8985C]" /> {totals.credits}
            </div>
          </div>
        </div>
      </section>

      {/* Search */}
      {invoices.length > 4 && (
        <div className="relative max-w-md">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
            <Search className="w-4 h-4" />
          </div>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per numero fattura o pacchetto"
            data-testid="billing-search"
            className="w-full h-10 pl-9 pr-3 rounded-md border border-[#E8E4D9] bg-white text-sm focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/10 outline-none transition-colors"
          />
        </div>
      )}

      {/* List */}
      <section className="rounded-xl border border-[#E8E4D9] bg-white overflow-hidden">
        {loading ? (
          <div className="p-12 grid place-items-center">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="p-10 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#F5F2EA] grid place-items-center mb-4">
              <FileText className="w-6 h-6 text-zinc-400" />
            </div>
            <h3 className="font-display font-semibold text-[#0A0A0A] mb-1">Nessuna fattura ancora</h3>
            <p className="text-sm text-zinc-500 max-w-sm mx-auto">
              Le fatture verranno generate automaticamente al primo acquisto crediti. Nessuna azione richiesta da parte tua.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">
            Nessun risultato per <span className="font-mono text-zinc-700">&ldquo;{q}&rdquo;</span>
          </div>
        ) : (
          <ul className="divide-y divide-[#E8E4D9]" data-testid="billing-list">
            {paged.map((inv) => {
              const StatusIcon = (STATUS_META[inv.status] || STATUS_META.not_transmitted).icon;
              const tone = (STATUS_META[inv.status] || STATUS_META.not_transmitted).tone;
              const label = (STATUS_META[inv.status] || STATUS_META.not_transmitted).label;
              return (
                <li
                  key={inv.id}
                  data-testid={`billing-row-${inv.id}`}
                  className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-sm font-bold text-[#0A0A0A]">{inv.full_number}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${tone}`}>
                        <StatusIcon className="w-3 h-3" /> {label}
                      </span>
                    </div>
                    <div className="text-sm text-zinc-700 truncate">
                      {inv.package_name || "Pacchetto crediti"}
                      {inv.credits ? (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-[#B8985C] font-mono">
                          <Coins className="w-3 h-3" /> {inv.credits} crediti
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1.5 flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {formatDate(inv.data)}
                      </span>
                      <span className="inline-flex items-center gap-1 font-semibold text-[#0A0A0A]">
                        <Euro className="w-3 h-3" /> {FMT_EUR.format(inv.amount_gross_eur || 0)}
                      </span>
                      {typeof inv.amount_net_eur === "number" && (
                        <span className="text-zinc-400">
                          (imponibile {FMT_EUR.format(inv.amount_net_eur)} + IVA {FMT_EUR.format(inv.vat_amount_eur || 0)})
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadXml(inv.id, inv.full_number)}
                    disabled={downloading === inv.id}
                    data-testid={`billing-download-${inv.id}`}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-[#1D4ED8] disabled:opacity-60 transition-colors"
                  >
                    {downloading === inv.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Download className="w-3.5 h-3.5" />}
                    Scarica XML
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {/* Paginatore — appare solo se ci sono più di 10 risultati filtrati */}
        {!loading && totalPages > 1 && (
          <div
            className="border-t border-[#E8E4D9] px-4 sm:px-5 py-3 flex items-center justify-between text-sm bg-[#FAF9F6]"
            data-testid="billing-pagination"
          >
            <div className="text-[#737373]">
              Pagina <span className="font-semibold text-[#0A0A0A]">{page + 1}</span> di{" "}
              <span className="font-semibold text-[#0A0A0A]">{totalPages}</span>
              <span className="hidden sm:inline text-[#A3A3A3] ml-2">
                ({pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} su {filtered.length})
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                data-testid="billing-prev-page"
                className="inline-flex items-center gap-1 h-8 pl-2 pr-3 rounded-full border border-[#E8E4D9] bg-white text-[#0A0A0A] hover:bg-[#F5F2EA] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Precedente
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                data-testid="billing-next-page"
                className="inline-flex items-center gap-1 h-8 pl-3 pr-2 rounded-full border border-[#E8E4D9] bg-white text-[#0A0A0A] hover:bg-[#F5F2EA] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Successiva <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Info footer */}
      <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
        <Info className="w-4 h-4 text-[#1D4ED8] mt-0.5 shrink-0" />
        <div className="text-xs text-blue-900 leading-relaxed">
          <strong>Nota fiscale.</strong> Le fatture con stato &laquo;In coda&raquo; verranno trasmesse al Sistema di Interscambio entro 12 giorni dalla data del documento (art. 21 DPR 633/72). La copia XML che scarichi da qui è identica a quella che troverai nel tuo cassetto fiscale una volta trasmessa. Per problemi o richieste specifiche scrivi a{" "}
          <a href="mailto:billing@dasabo.com" className="font-semibold underline hover:no-underline" data-testid="billing-support-mailto">
            billing@dasabo.com <ExternalLink className="inline w-3 h-3" />
          </a>.
        </div>
      </div>
    </div>
  );
}
